import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
  ElementRef,
  ViewChild,
} from '@angular/core'
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  ValidationErrors,
} from '@angular/forms'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslationService } from '../../../../services/translation.service'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatButtonModule } from '@angular/material/button'
import { MatSelectModule } from '@angular/material/select'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatIcon } from '@angular/material/icon'
import { ProductCsvTemplatesService } from '../../../../services/productCsvTemplate.service'
import {
  CsvTemplateCustomField,
  ProductCsvTemplate,
} from '../../../../types/csvTemplates'
import { SpinnerService } from '../../../../services/spinner.service'
import { ErrorService } from '../../../../services/error.service'
import { Dialog } from '@angular/cdk/dialog'
import { DialogService } from '../../../../services/dialog.service'
import { ApiError } from '../../../../types/apiError'
import { firstValueFrom } from 'rxjs'
import { productCsvMappingFields } from '../../../../helpers/product-csv-template-fields'
import { trimCustomFieldText } from '../../../../helpers/custom-fields'

type CsvCustomFieldForm = FormGroup<{
  name: FormControl<string>
  column: FormControl<string>
  type: FormControl<CsvTemplateCustomField['type']>
  unit: FormControl<string>
}>

@Component({
  selector: 'app-create-product-template',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInputModule,
    MatButtonModule,
    MatIcon,
    MatSelectModule,
    MatTooltipModule,
  ],
  templateUrl: './create-product-template.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-product-template.component.scss',
})
export class CreateProductTemplateComponent implements OnInit {
  translationService = inject(TranslationService)
  router = inject(Router)
  private fb = inject(FormBuilder)
  private spinnerService = inject(SpinnerService)
  private dialog = inject(Dialog)
  private dialogService = inject(DialogService)
  private errorService = inject(ErrorService)
  private productCsvTemplatesService = inject(ProductCsvTemplatesService)
  private route = inject(ActivatedRoute)

  @ViewChild('additionalMappings')
  private additionalMappings?: ElementRef<HTMLDetailsElement>

  customFields = new FormArray<CsvCustomFieldForm>([])

  templateForm = this.fb.group({
    customFields: this.customFields,
    name: ['', Validators.required],
    articleName: ['Article Name', Validators.required],
    otherNames: ['Other Names'],
    primaryArticleIdentifier: ['Product Identifier', Validators.required],
    articleCategory: ['Article Category'],
    productionInCanada: ['Produced in Canada'],
    picture: ['Picture'],
    safeUseInstructions: ['Safe Use Instructions'],
    disassemblingInstructions: ['Disassembling Instructions'],
    brand: ['Brand'],
    marketplaceCategory: ['', this.validateMappingHeading],
    marketplaceSubcategory: ['', this.validateMappingHeading],
    requestUnit: ['', this.validateMappingHeading],
    priceAmount: ['', [Validators.required, this.validateMappingHeading]],
    priceCurrency: ['', [Validators.required, this.validateMappingHeading]],
    condition: ['', this.validateMappingHeading],
    droneFlightTimeMinutes: ['', this.validateMappingHeading],
    droneType: ['', this.validateMappingHeading],
    droneVideoResolution: ['', this.validateMappingHeading],
    droneSensorSize: ['', this.validateMappingHeading],
    droneFpv: ['', this.validateMappingHeading],
    allowRequests: ['', this.validateMappingHeading],
    isMarketplacePrivate: ['', this.validateMappingHeading],
    sku: ['SKU'],
    batch: ['Lower Batch Range'],
    batchUpper: ['Upper Batch Range'],
    serial: ['Lower Serial Range'],
    serialUpper: ['Upper Serial Range'],
    description: ['Description'],
    safetyDataSheet: ['Safety Data Sheet'],
    measureValue: ['Measure Value'],
    measureUnit: ['Measure Unit'],
    gs1Identifier: ['GS1 Identifier'],
    categoryCode: ['Category Code'],
    codeDescription: ['Code Description'],
    diameter: ['Diameter'],
    width: ['Width'],
    height: ['Height'],
    recyclingInstructions: ['Recycling Instructions'],
    type: ['Type'],
    barcodeIdentifier: ['Barcode Identifier'],
    englishShortName: ['English Short Name'],
    frenchShortName: ['French Short Name'],
    ingredientName: ['Ingredient Name'],
    mayContain: ['May Contain'],
    doesNotContain: ['Does Not Contain'],
    optionallyDisclosed: ['Optionally Disclosed'],
    upperConcentration: ['Upper Concentration'],
    lowerConcentration: ['Lower Concentration'],
    complianceOfficer: ['Compliance Officer'],
    wasteHandler: ['Waste Handler'],
    consumer: ['Consumer'],
    productIdentifier: ['Product Identifier'],
    fullIngredientDisclosure: ['Full Ingredient Disclosure'],
    isRecyclable: ['Is Recyclable'],
    isPackage: ['Is Package'],
    packagingType: ['Packaging Type'],
    recycledContentPercentage: ['Recycled Content Percentage'],
    alberta: ['Alberta'],
    britishColumbia: ['British Columbia'],
    manitoba: ['Manitoba'],
    newBrunswick: ['New Brunswick'],
    newfoundlandAndLabrador: ['Newfoundland and Labrador'],
    northwestTerritories: ['Northwest Territories'],
    novaScotia: ['Nova Scotia'],
    nunavut: ['Nunavut'],
    ontario: ['Ontario'],
    princeEdwardIsland: ['Prince Edward Island'],
    quebec: ['Quebec'],
    saskatchewan: ['Saskatchewan'],
    yukon: ['Yukon'],
    hongKong: ['Hong Kong'],
    toxic: ['Toxic'],
    flammable: ['Flammable'],
    explosive: ['Explosive'],
    environmentalHazard: ['Environmental Hazard'],
    healthHazard: ['Health Hazard'],
    harmful: ['Harmful'],
    corrosive: ['Corrosive'],
    oxidizing: ['Oxidizing'],
    napcsCode: ['NAPCS Code'],
    productWeightKg: ['Product Weight (kg)'],
    sourceType: ['Source Type'],
    isResidentialWaste: ['Is Residential Waste'],
    isIciWaste: ['Is Industrial, Commercial and Institutional Waste'],
    isCdrWaste: ['Is Construction, Demolition, and Renovation Waste'],
    supplier: ['Supplier'],
    category: ['Category'],
    subcategory: ['Subcategory'],
    calculationMethod: ['Calculation Method'],
    calculationMethodOther: ['Other Calculation Method'],
    reportingDate: ['Reporting Date'],
    tags: ['Tags'],
    isPlasticPackaging: ['Is Plastic Packaging'],
    materialType: ['Material Type'],
    componentParentSku: ['Parent SKU'],
    componentSku: ['Component SKU'],
    componentName: ['Component Name'],
    componentGroupName: ['Component Group Name'],
    componentQuantity: ['Component Quantity'],
    componentSupplier: ['Component Supplier'],
  })

  title = this.translationService.getCreateCSVTemplateText()
  selectedPreset: 'marketplace' | 'chemicalRecycling' = 'marketplace'

  private fullDefaults = this.templateForm.getRawValue()
  private fields = productCsvMappingFields(this.translationService)
  requiredFields = this.fields.filter((field) =>
    [
      'articleName',
      'primaryArticleIdentifier',
      'priceAmount',
      'priceCurrency',
      'marketplaceCategory',
      'requestUnit',
    ].includes(field.name),
  )
  optionalFields = this.fields.filter((field) =>
    [
      'marketplaceSubcategory',
      'brand',
      'sku',
      'otherNames',
      'picture',
      'description',
      'measureValue',
      'measureUnit',
      'condition',
      'allowRequests',
      'isMarketplacePrivate',
    ].includes(field.name),
  )
  droneFields = this.fields.filter((field) => field.name.startsWith('drone'))
  additionalFields = this.fields.filter(
    (field) =>
      ![
        ...this.requiredFields,
        ...this.optionalFields,
        ...this.droneFields,
      ].includes(field),
  )

  /**
   * Applies a mapping preset, records the selection and preserves the template name.
   * @param {boolean} marketplace Whether to use marketplace mappings instead of all product mappings.
   */
  useDefaults(marketplace: boolean): void {
    const name = this.templateForm.controls.name.value
    this.customFields.clear()
    this.templateForm.reset(this.fullDefaults)
    if (marketplace) {
      for (const control of Object.values(this.templateForm.controls)) {
        if (control instanceof FormControl) control.setValue('')
      }
      this.templateForm.patchValue({
        articleName: 'Product name',
        primaryArticleIdentifier: 'Product identifier',
        marketplaceCategory: 'Marketplace category',
        marketplaceSubcategory: 'Marketplace subcategory',
        requestUnit: 'Request unit',
        brand: 'Brand',
        picture: 'Picture',
        measureValue: 'Weight',
        measureUnit: 'Weight unit',
        condition: 'Condition',
        allowRequests: 'Allow requests',
        isMarketplacePrivate: 'Private',
        droneFlightTimeMinutes: 'Flight time (minutes)',
        droneType: 'Drone type',
        droneVideoResolution: 'Video resolution',
        droneSensorSize: 'Sensor size',
        droneFpv: 'FPV',
      })
    }
    this.templateForm.patchValue({
      name,
      priceAmount: 'Price',
      priceCurrency: 'Currency',
    })
    this.selectedPreset = marketplace ? 'marketplace' : 'chemicalRecycling'
  }

  constructor() {
    this.templateForm.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.updateConditionalValidators()
    })
  }

  /**
   * Enforces the API's 128-byte limit for configured CSV headings.
   * @param {AbstractControl} control Heading control to validate.
   * @returns {ValidationErrors | null} A length error when the trimmed UTF-8 value exceeds the limit.
   */
  private validateMappingHeading(
    control: AbstractControl,
  ): ValidationErrors | null {
    const value = trimCustomFieldText(control.value ?? '')
    return new TextEncoder().encode(value).length > 128
      ? { tooLong: true }
      : null
  }

  /**
   * Adds one named CSV column with its product field definition.
   * @param {CsvTemplateCustomField} [field] Saved definition to load when editing.
   */
  addCustomField(field?: CsvTemplateCustomField): void {
    if (this.customFields.length >= 50) return
    this.customFields.push(
      new FormGroup({
        name: new FormControl(field?.name ?? '', {
          nonNullable: true,
          validators: [Validators.required, this.validateMappingHeading],
        }),
        column: new FormControl(field?.column ?? field?.name ?? '', {
          nonNullable: true,
          validators: [Validators.required, this.validateMappingHeading],
        }),
        type: new FormControl<CsvTemplateCustomField['type']>(
          field?.type ?? 'text',
          { nonNullable: true, validators: Validators.required },
        ),
        unit: new FormControl(field?.unit ?? '', {
          nonNullable: true,
          validators: (control) =>
            new TextEncoder().encode(trimCustomFieldText(control.value))
              .length > 64
              ? { mapping: 'invalidUnit' }
              : null,
        }),
      }),
    )
  }

  /**
   * Removes a column definition from this template without changing saved products.
   * @param {number} index Position of the definition to remove.
   */
  removeCustomField(index: number): void {
    this.customFields.removeAt(index)
  }

  /**
   * Defaults an empty CSV heading to the field name.
   * @param {number} index Position of the edited definition.
   */
  syncCustomFieldColumn(index: number): void {
    const controls = this.customFields.at(index).controls
    if (!trimCustomFieldText(controls.column.value)) {
      controls.column.setValue(trimCustomFieldText(controls.name.value))
    }
  }

  /**
   * Clears units when a field no longer represents a number.
   * @param {number} index Position of the definition whose type changed.
   */
  onCustomFieldTypeChange(index: number): void {
    const controls = this.customFields.at(index).controls
    if (controls.type.value !== 'number') controls.unit.setValue('')
  }

  /**
   * Requires category and request unit when either mapping or a subcategory is configured.
   * Updates controls without emitting further value changes.
   */
  private updateConditionalValidators(): void {
    const controls = this.templateForm.controls
    const enabled = [
      controls.marketplaceCategory,
      controls.marketplaceSubcategory,
      controls.requestUnit,
    ].some((control) => trimCustomFieldText(control.value ?? ''))
    for (const control of [
      controls.marketplaceCategory,
      controls.requestUnit,
    ]) {
      if (enabled) control.addValidators(Validators.required)
      else control.removeValidators(Validators.required)
      control.updateValueAndValidity({ emitEvent: false })
    }
  }

  /**
   * Returns the translated mapping error, preferring field errors from the API.
   * @param {string} name Name of the form control.
   * @returns {string} The translated validation message.
   */
  fieldError(name: string): string {
    const control = this.templateForm.get(name)
    if (
      name.startsWith('customFields.') &&
      name.endsWith('.name') &&
      control?.getError('mapping') === 'duplicate'
    ) {
      return this.translationService.getCustomFieldDuplicateErrorText()
    }
    return this.translationService.getCsvMappingErrorText(
      control?.getError('mapping') ||
        (control?.hasError('tooLong') ? 'tooLong' : 'required'),
    )
  }

  /**
   * Opens optional mappings and focuses the first invalid field so its error is visible.
   */
  private revealAdditionalMappingErrors(): void {
    const field = this.additionalFields.find(
      (field) => this.templateForm.get(field.name)?.invalid,
    )
    const section = this.additionalMappings?.nativeElement
    if (!field || !section) return

    section.open = true
    section
      .querySelector<HTMLInputElement>(`[data-mapping-field="${field.name}"]`)
      ?.focus()
  }

  isEditMode: boolean = false
  templateId: string | null = null
  templateForEdit?: ProductCsvTemplate

  ngOnInit(): void {
    this.route.paramMap.subscribe(async (params) => {
      const templateId = params.get('id')
      this.isEditMode = !!templateId

      if (this.isEditMode) {
        this.title = this.translationService.getEditCSVTemplateText()
        await this.setTemplate(templateId!)
      } else {
        this.useDefaults(true)
      }
    })
  }

  /**
   * Sets the template data for Edit Mode
   * @param {string} templateId Template id from route params
   */
  async setTemplate(templateId: string) {
    try {
      this.templateId = templateId
      this.templateForEdit = await firstValueFrom(
        this.productCsvTemplatesService.getById(this.templateId),
      )

      this.setFields()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Sets UI fields for Edit Mode
   */
  setFields() {
    if (this.templateForEdit) {
      const templateData: Partial<ProductCsvTemplate> = {
        ...this.templateForEdit,
      }
      this.customFields.clear()
      this.templateForm.reset({
        ...this.fullDefaults,
        ...templateData,
        priceAmount: templateData.priceAmount || 'Price',
        priceCurrency: templateData.priceCurrency || 'Currency',
        customFields: [],
      })
      for (const field of templateData.customFields ?? [])
        this.addCustomField(field)
    }
  }

  /**
   * Trims and validates mappings before creating or updating the template.
   * Displays API mapping errors beside their corresponding form controls.
   */
  async onSubmit() {
    this.customFields.controls.forEach((_, index) =>
      this.syncCustomFieldColumn(index),
    )
    const { customFields, ...mappings } = this.templateForm.getRawValue()
    this.templateForm.patchValue({
      ...Object.fromEntries(
        Object.entries(mappings).map(([name, value]) => [
          name,
          trimCustomFieldText(value ?? ''),
        ]),
      ),
      customFields: customFields.map((field) => ({
        ...field,
        name: trimCustomFieldText(field.name),
        column: trimCustomFieldText(field.column),
        unit: trimCustomFieldText(field.unit),
      })),
    })
    this.templateForm.markAllAsTouched()
    if (this.templateForm.invalid || (this.isEditMode && !this.templateId)) {
      this.revealAdditionalMappingErrors()
      return
    }

    this.spinnerService.startSpinner()
    try {
      const template = this.templateForm.getRawValue() as ProductCsvTemplate
      let successMsg = this.translationService.getCSVTemplateCreatedText()
      if (this.isEditMode) {
        await firstValueFrom(
          this.productCsvTemplatesService.edit(this.templateId!, template),
        )
        successMsg = this.translationService.getCSVTemplateUpdatedText()
      } else {
        await firstValueFrom(this.productCsvTemplatesService.create(template))
      }
      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getProductCSVTemplatesText(),
        successMsg,
        undefined,
        this.router,
        'settings',
      )
    } catch (err) {
      const apiErr = err as ApiError
      if (apiErr.name === 'InvalidCsvTemplate' && apiErr.details) {
        for (const [field, problem] of Object.entries(apiErr.details)) {
          const control = this.templateForm.get(field)
          control?.setErrors({ mapping: problem })
          control?.markAsTouched()
        }
        this.revealAdditionalMappingErrors()
        return
      }
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    } finally {
      this.spinnerService.stopSpinner()
    }
  }

  /**
   * Resets form data
   */
  onReset() {
    if (!this.isEditMode) {
      this.useDefaults(true)
      this.templateForm.controls.name.setValue('')
    } else {
      this.setFields()
    }
  }
}
